/**
 * AccountDeletionDialog component
 * Requirements: 10.2 - Account deletion with confirmation process
 */
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { deleteAccount } from '../api/privacyApi';

interface AccountDeletionDialogProps {
  open: boolean;
  onClose: () => void;
  onDeleted: () => void;
}

const CONFIRMATION_TEXT = '削除';

export const AccountDeletionDialog = ({
  open,
  onClose,
  onDeleted,
}: AccountDeletionDialogProps) => {
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setConfirmText('');
      setError(null);
    }
  }, [open]);

  const isConfirmed = confirmText === CONFIRMATION_TEXT;

  const handleDelete = async () => {
    if (!isConfirmed) return;

    setLoading(true);
    setError(null);

    try {
      await deleteAccount();
      onDeleted();
    } catch {
      setError('削除に失敗しました。もう一度お試しください。');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningAmberIcon color="error" />
        アカウント削除
      </DialogTitle>
      <DialogContent>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>警告:</strong>{' '}
            この操作を実行すると、すべてのデータが完全に削除されます。この操作は取り消せません。
          </Typography>
        </Alert>

        <Typography variant="body2" paragraph>
          以下のデータが削除されます:
        </Typography>
        <Box component="ul" sx={{ mt: 0, mb: 2 }}>
          <li>
            <Typography variant="body2">アカウント情報</Typography>
          </li>
          <li>
            <Typography variant="body2">質問への回答履歴</Typography>
          </li>
          <li>
            <Typography variant="body2">分析結果</Typography>
          </li>
        </Box>

        <TextField
          fullWidth
          label={`確認のため「${CONFIRMATION_TEXT}」と入力してください`}
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          error={!!error}
          helperText={error}
          disabled={loading}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          キャンセル
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDelete}
          disabled={!isConfirmed || loading}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
        >
          削除する
        </Button>
      </DialogActions>
    </Dialog>
  );
};
